/**
 * 3D Scene for warehouse layout visualization using React Three Fiber.
 * Uses context for state. Performance improvements: conditional useFrame, React.memo on Rack3D.
 */

import React, { useRef, useMemo, useState, memo } from 'react';
import { Canvas, useFrame, ThreeEvent } from '@react-three/fiber';
import { OrbitControls, Grid, Text, PerspectiveCamera, Environment } from '@react-three/drei';
import * as THREE from 'three';
import { useLayoutEditor } from '../contexts/LayoutEditorContext';
import {
  RackPosition,
  RackDimensions,
  LocationActivity,
  HeatmapData,
  ACTIVITY_COLORS,
  ActivityType,
  WarehouseObject as WarehouseObjectShape,
} from '../types/warehouseLayout';
import {
  resolveBinRenderInfo,
  PlacedBinRenderInfo,
  OBJECT_TYPE_COLORS,
} from '../utils/layoutApiMapping';

// ============================================
// Rack 3D Component (memoized)
// ============================================

export interface Rack3DProps {
  position: RackPosition;
  dimensions: RackDimensions;
  isSelected: boolean;
  onClick: () => void;
  heatmapIntensity?: number;
  activities?: LocationActivity[];
  showHeatmap: boolean;
  showActivity: boolean;
}

export const Rack3D: React.FC<Rack3DProps> = memo(({
  position,
  dimensions,
  isSelected,
  onClick,
  heatmapIntensity = 0,
  activities = [],
  showHeatmap,
  showActivity,
}) => {
  const meshRef = useRef<THREE.Mesh>(null);
  const [hovered, setHovered] = useState(false);

  // Only run useFrame when there are activities to animate
  useFrame((frameState) => {
    if (!meshRef.current) return;
    if (showActivity && activities.length > 0) {
      const pulse = Math.sin(frameState.clock.elapsedTime * 3) * 0.05;
      meshRef.current.scale.setScalar(1 + pulse);
    } else {
      meshRef.current.scale.setScalar(1);
    }
  });

  const color = useMemo(() => {
    if (showHeatmap && heatmapIntensity > 0) {
      const r = Math.round(255 * heatmapIntensity);
      const g = Math.round(100 * (1 - heatmapIntensity));
      const b = Math.round(50 * (1 - heatmapIntensity));
      return `rgb(${r}, ${g}, ${b})`;
    }
    if (activities.length > 0) {
      return ACTIVITY_COLORS[activities[0].activityType as ActivityType] || '#3b82f6';
    }
    // Egendefinert farge fra egenskaps-panelet — samme grunnfarge som 2D-visningen
    return position.color || '#3b82f6';
  }, [showHeatmap, heatmapIntensity, activities, position.color]);

  const rotationRadians: [number, number, number] = [0, (position.rotation * Math.PI) / 180, 0];

  // Pre-compute shelf geometries
  const shelfPositions = useMemo(() => {
    return Array.from({ length: dimensions.shelves }, (_, i) => ({
      y: -dimensions.height / 2 + (i + 1) * (dimensions.height / dimensions.shelves),
    }));
  }, [dimensions.shelves, dimensions.height]);

  const dividerPositions = useMemo(() => {
    return Array.from({ length: dimensions.verticals - 1 }, (_, i) => ({
      x: -dimensions.width / 2 + (i + 1) * (dimensions.width / dimensions.verticals),
    }));
  }, [dimensions.verticals, dimensions.width]);

  return (
    <group position={[position.x, dimensions.height / 2, position.y]} rotation={rotationRadians}>
      {/* Main rack body */}
      <mesh
        ref={meshRef}
        onClick={(e: ThreeEvent<MouseEvent>) => {
          e.stopPropagation();
          onClick();
        }}
        onPointerOver={() => setHovered(true)}
        onPointerOut={() => setHovered(false)}
      >
        <boxGeometry args={[dimensions.width, dimensions.height, dimensions.depth]} />
        <meshStandardMaterial
          color={color}
          transparent
          opacity={hovered ? 0.9 : 0.75}
          metalness={0.1}
          roughness={0.8}
        />
      </mesh>

      {/* Selection outline */}
      {isSelected && (
        <lineSegments>
          <edgesGeometry args={[new THREE.BoxGeometry(dimensions.width + 0.1, dimensions.height + 0.1, dimensions.depth + 0.1)]} />
          <lineBasicMaterial color="#fbbf24" linewidth={2} />
        </lineSegments>
      )}

      {/* Shelf lines */}
      {shelfPositions.map((shelf, i) => (
        <mesh key={`shelf-${i}`} position={[0, shelf.y, 0]}>
          <boxGeometry args={[dimensions.width - 0.02, 0.02, dimensions.depth - 0.02]} />
          <meshStandardMaterial color="#1e3a5f" />
        </mesh>
      ))}

      {/* Vertical dividers */}
      {dividerPositions.map((div, i) => (
        <mesh key={`div-${i}`} position={[div.x, 0, 0]}>
          <boxGeometry args={[0.02, dimensions.height, dimensions.depth - 0.02]} />
          <meshStandardMaterial color="#1e3a5f" opacity={0.5} transparent />
        </mesh>
      ))}

      {/* Rack label */}
      <Text
        position={[0, dimensions.height / 2 + 0.3, 0]}
        fontSize={0.4}
        color="white"
        anchorX="center"
        anchorY="middle"
      >
        {position.label || position.rackNumber}
      </Text>

      {/* Activity indicator */}
      {showActivity && activities.length > 0 && (
        <mesh position={[dimensions.width / 2 - 0.2, dimensions.height / 2 + 0.1, dimensions.depth / 2 - 0.2]}>
          <sphereGeometry args={[0.15, 16, 16]} />
          <meshStandardMaterial
            color={ACTIVITY_COLORS[activities[0].activityType as ActivityType] || '#22c55e'}
            emissive={ACTIVITY_COLORS[activities[0].activityType as ActivityType] || '#22c55e'}
            emissiveIntensity={0.5}
          />
        </mesh>
      )}
    </group>
  );
}, (prevProps, nextProps) => {
  // Custom comparison for React.memo
  return (
    prevProps.position.x === nextProps.position.x &&
    prevProps.position.y === nextProps.position.y &&
    prevProps.position.rotation === nextProps.position.rotation &&
    prevProps.position.rackNumber === nextProps.position.rackNumber &&
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.showHeatmap === nextProps.showHeatmap &&
    prevProps.showActivity === nextProps.showActivity &&
    prevProps.heatmapIntensity === nextProps.heatmapIntensity &&
    prevProps.activities?.length === nextProps.activities?.length &&
    prevProps.dimensions.width === nextProps.dimensions.width &&
    prevProps.dimensions.depth === nextProps.dimensions.depth &&
    prevProps.dimensions.height === nextProps.dimensions.height
  );
});

Rack3D.displayName = 'Rack3D';

// ============================================
// Warehouse Floor Component
// ============================================

export const WarehouseFloor: React.FC<{ length: number; width: number }> = ({ length, width }) => (
  <group>
    <mesh rotation={[-Math.PI / 2, 0, 0]} position={[length / 2, -0.01, width / 2]} receiveShadow>
      <planeGeometry args={[length, width]} />
      <meshStandardMaterial color="#374151" />
    </mesh>
    <Grid
      args={[length, width]}
      position={[length / 2, 0, width / 2]}
      cellSize={1}
      cellThickness={0.5}
      cellColor="#4b5563"
      sectionSize={5}
      sectionThickness={1}
      sectionColor="#6b7280"
      fadeDistance={100}
      infiniteGrid={false}
    />
    <lineSegments position={[length / 2, 0.01, width / 2]}>
      <edgesGeometry args={[new THREE.PlaneGeometry(length, width)]} />
      <lineBasicMaterial color="#9ca3af" />
    </lineSegments>
  </group>
);

// ============================================
// Faste objekter (søyler, porter, stasjoner)
// ============================================

const LayoutObject3D: React.FC<{ object: WarehouseObjectShape }> = ({ object }) => {
  const color = object.color || OBJECT_TYPE_COLORS[object.type] || '#6b7280';
  const height = object.height ?? 1.0;
  const rotationRadians: [number, number, number] = [0, ((object.rotation ?? 0) * Math.PI) / 180, 0];

  return (
    <group position={[object.x, height / 2, object.y]} rotation={rotationRadians}>
      <mesh castShadow>
        <boxGeometry args={[object.width, height, object.depth]} />
        <meshStandardMaterial color={color} transparent opacity={0.75} />
      </mesh>
      {object.name && (
        <Text
          position={[0, height / 2 + 0.4, 0]}
          fontSize={0.35}
          color="#e5e7eb"
          anchorX="center"
        >
          {object.name}
        </Text>
      )}
    </group>
  );
};

// ============================================
// Enkeltlokasjoner (gulv/vegg/heng/skap/dock)
// ============================================

const PlacedBin3D: React.FC<{ bin: PlacedBinRenderInfo }> = ({ bin }) => {
  const rotationRadians: [number, number, number] = [0, (bin.rotation * Math.PI) / 180, 0];

  return (
    <group position={[bin.x, bin.heightM / 2, bin.y]} rotation={rotationRadians}>
      <mesh castShadow>
        <boxGeometry args={[bin.widthM, bin.heightM, bin.depthM]} />
        <meshStandardMaterial color={bin.color} transparent opacity={0.55} />
      </mesh>
      <lineSegments>
        <edgesGeometry args={[new THREE.BoxGeometry(bin.widthM, bin.heightM, bin.depthM)]} />
        <lineBasicMaterial color={bin.color} />
      </lineSegments>
      <Text
        position={[0, bin.heightM / 2 + 0.3, 0]}
        fontSize={0.3}
        color="#f3f4f6"
        anchorX="center"
      >
        {bin.binCode}
      </Text>
    </group>
  );
};

// ============================================
// Main 3D Scene Component
// ============================================

const Layout3DScene: React.FC = () => {
  const { state, dispatch, rackDimensionsMap } = useLayoutEditor();
  const { layout, editorState } = state;

  // Enkeltlokasjoner med mål fra StcBinSize
  const binRenderInfos = useMemo(() => {
    const specialByCode = new Map(state.specialBins.map(b => [b.binCode, b]));
    return state.binPositions.map(p => resolveBinRenderInfo(p, specialByCode));
  }, [state.binPositions, state.specialBins]);

  // Stub data (Phase 2 integration)
  const heatmapData: HeatmapData[] = [];
  const activities: LocationActivity[] = [];

  const heatmapLookup = useMemo(() => {
    const lookup = new Map<string, number>();
    heatmapData.forEach(data => {
      lookup.set(data.rackNumber, Math.max(lookup.get(data.rackNumber) || 0, data.intensity));
    });
    return lookup;
  }, [heatmapData]);

  const activityLookup = useMemo(() => {
    const lookup = new Map<string, LocationActivity[]>();
    activities.forEach(activity => {
      const key = activity.rackNumber;
      if (!lookup.has(key)) lookup.set(key, []);
      lookup.get(key)!.push(activity);
    });
    return lookup;
  }, [activities]);

  // Calculate bounding box of all placed racks for optimal camera framing
  // Must be called before early return to satisfy rules-of-hooks
  const rackExtent = useMemo(() => {
    if (!layout || layout.racks.length === 0) {
      const dims = layout?.dimensions;
      const l = dims?.length ?? 30;
      const w = dims?.width ?? 20;
      return { centerX: l / 2, centerZ: w / 2, span: Math.max(l, w) };
    }
    let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity, maxH = 0;
    layout.racks.forEach(rack => {
      const dim = rackDimensionsMap.get(rack.rackNumber) || { width: 6.8, depth: 1.3, height: 2.7, verticals: 10, shelves: 5 };
      const rotated = (rack.rotation === 90 || rack.rotation === 270)
        ? { w: dim.depth, d: dim.width } : { w: dim.width, d: dim.depth };
      minX = Math.min(minX, rack.x - rotated.w / 2);
      maxX = Math.max(maxX, rack.x + rotated.w / 2);
      minZ = Math.min(minZ, rack.y - rotated.d / 2);
      maxZ = Math.max(maxZ, rack.y + rotated.d / 2);
      maxH = Math.max(maxH, dim.height);
    });
    const span = Math.max(maxX - minX, maxZ - minZ, 10);
    return { centerX: (minX + maxX) / 2, centerZ: (minZ + maxZ) / 2, span, maxH };
  }, [layout, rackDimensionsMap]);

  if (!layout) return null;

  const { dimensions, racks } = layout;

  // Camera positioned to frame the racks nicely - closer and more angled
  const cameraPosition: [number, number, number] = [
    rackExtent.centerX + rackExtent.span * 0.3,
    rackExtent.span * 0.6,
    rackExtent.centerZ + rackExtent.span * 0.8,
  ];
  const cameraTarget: [number, number, number] = [rackExtent.centerX, 0, rackExtent.centerZ];

  return (
    <div className="w-full h-full bg-gray-900 rounded-lg overflow-hidden">
      <Canvas shadows>
        <PerspectiveCamera makeDefault position={cameraPosition} fov={55} />

        <ambientLight intensity={0.5} />
        <directionalLight
          position={[rackExtent.centerX + rackExtent.span, 15, rackExtent.centerZ + rackExtent.span]}
          intensity={0.9}
          castShadow
          shadow-mapSize={[2048, 2048]}
        />
        <pointLight position={[rackExtent.centerX, 8, rackExtent.centerZ]} intensity={0.4} />

        <Environment preset="warehouse" />

        <OrbitControls
          target={cameraTarget}
          maxPolarAngle={Math.PI / 2 - 0.05}
          minDistance={2}
          maxDistance={rackExtent.span * 3}
          enableDamping
          dampingFactor={0.05}
        />

        <WarehouseFloor length={dimensions.length} width={dimensions.width} />

        {racks.map(rack => {
          const dim = rackDimensionsMap.get(rack.rackNumber) || {
            width: 6.8, depth: 1.3, height: 2.7, verticals: 10, shelves: 5,
          };
          return (
            <Rack3D
              key={rack.rackNumber}
              position={rack}
              dimensions={dim}
              isSelected={editorState.selectedRacks.has(rack.rackNumber)}
              onClick={() => dispatch({
                type: 'SET_SELECTION',
                selectedRacks: new Set([rack.rackNumber]),
              })}
              heatmapIntensity={heatmapLookup.get(rack.rackNumber)}
              activities={activityLookup.get(rack.rackNumber)}
              showHeatmap={editorState.showHeatmap}
              showActivity={editorState.showActivity}
            />
          );
        })}

        {layout.objects.map(obj => (
          <LayoutObject3D key={obj.id} object={obj} />
        ))}

        {binRenderInfos.map(bin => (
          <PlacedBin3D key={bin.binCode} bin={bin} />
        ))}

        <Text position={[dimensions.length / 2, 0.5, -1]} fontSize={0.8} color="#9ca3af" anchorX="center">
          {dimensions.length}m
        </Text>
        <Text position={[-1, 0.5, dimensions.width / 2]} fontSize={0.8} color="#9ca3af" anchorX="center" rotation={[0, Math.PI / 2, 0]}>
          {dimensions.width}m
        </Text>

        <mesh position={[0, 0.1, 0]}>
          <sphereGeometry args={[0.2, 16, 16]} />
          <meshStandardMaterial color="#ef4444" />
        </mesh>
      </Canvas>
    </div>
  );
};

export default Layout3DScene;
